import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { IoChevronDown } from "react-icons/io5";
import { buildAutomationChildTestId, buildAutomationOptionTestId, resolveAutomationTestId } from "../../InputField/testId";

const GroupDropdown = ({
  label,
  placeholder = "Select groups...",
  memberOptions = [],
  adminOptions = [],
  selectedMemberGroups = [],
  selectedAdminGroups = [],
  allMemberGroupsSelected = false,
  isMemberGroupSetIndeterminate = false,
  onToggleMemberGroupSet,
  onToggleMemberGroup,
  onToggleAdminGroup,
  onSelectAll,
  onClearAll,
  groupSetLabel = "Member (select all)",
  className = "",
  disabled = false,
  testId,
  id,
  "data-testid": dataTestId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlightIndex, setHighlightIndex] = useState(0);
  const containerRef = useRef(null);
  const controlRef = useRef(null);
  const searchRef = useRef(null);
  const groupSetRef = useRef(null);
  const optionRefs = useRef([]);
  const listId = useId();
  const fieldTestId = resolveAutomationTestId(dataTestId, testId, id);
  const visibleSelectedCount = selectedMemberGroups.length + selectedAdminGroups.length;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setQuery("");
      return undefined;
    }
    const frame = requestAnimationFrame(() => searchRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [isOpen]);

  useEffect(() => {
    if (groupSetRef.current) {
      groupSetRef.current.indeterminate = Boolean(isMemberGroupSetIndeterminate);
    }
  }, [isMemberGroupSetIndeterminate, isOpen]);

  const filteredMemberOptions = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return memberOptions;
    return memberOptions.filter((option) =>
      `${option.label ?? option.value ?? ""}`.toLowerCase().includes(needle)
    );
  }, [memberOptions, query]);

  const filteredAdminOptions = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return adminOptions;
    return adminOptions.filter((option) =>
      `${option.label ?? option.value ?? ""}`.toLowerCase().includes(needle)
    );
  }, [adminOptions, query]);

  const flatOptions = useMemo(() => {
    const items = [{ key: "group-set", type: "groupSet" }];
    filteredMemberOptions.forEach((option) => {
      items.push({ key: `member-${option.value}`, type: "member", option });
    });
    filteredAdminOptions.forEach((option) => {
      items.push({ key: `admin-${option.value}`, type: "admin", option });
    });
    return items;
  }, [filteredAdminOptions, filteredMemberOptions]);

  const optionIndexByKey = useMemo(() => {
    const map = new Map();
    flatOptions.forEach((item, index) => {
      map.set(item.key, index);
    });
    return map;
  }, [flatOptions]);

  useEffect(() => {
    optionRefs.current = [];
  }, [flatOptions]);

  useEffect(() => {
    if (!isOpen) return;
    if (!flatOptions.length) {
      setHighlightIndex(-1);
      return;
    }
    setHighlightIndex(0);
  }, [flatOptions.length, isOpen, query]);

  useEffect(() => {
    if (!isOpen) return;
    const node = optionRefs.current[highlightIndex];
    node?.scrollIntoView?.({ block: "nearest" });
  }, [highlightIndex, isOpen]);

  const displayValue = useMemo(() => {
    if (!visibleSelectedCount) return placeholder;
    const summary = [];
    if (selectedMemberGroups.length) {
      summary.push(allMemberGroupsSelected ? "All member groups" : `${selectedMemberGroups.length} member`);
    }
    if (selectedAdminGroups.length) {
      summary.push(`${selectedAdminGroups.length} admin`);
    }
    return summary.join(" | ");
  }, [
    allMemberGroupsSelected,
    placeholder,
    selectedAdminGroups.length,
    selectedMemberGroups.length,
    visibleSelectedCount,
  ]);

  const moveHighlight = (delta) => {
    if (!flatOptions.length) return;
    setHighlightIndex((prev) => {
      const base = prev < 0 ? 0 : prev;
      return (base + delta + flatOptions.length) % flatOptions.length;
    });
  };

  const selectHighlighted = () => {
    const item = flatOptions[highlightIndex];
    if (!item) return;
    if (item.type === "groupSet") {
      onToggleMemberGroupSet?.(!allMemberGroupsSelected);
      return;
    }
    if (item.type === "member") {
      onToggleMemberGroup?.(item.option.value);
      return;
    }
    if (item.type === "admin") {
      onToggleAdminGroup?.(item.option.value);
    }
  };

  const handleControlKeyDown = (event) => {
    if (disabled) return;
    if (!isOpen && (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ")) {
      event.preventDefault();
      setIsOpen(true);
      return;
    }
    if (!isOpen) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      moveHighlight(1);
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      moveHighlight(-1);
    }
    if (event.key === "Enter") {
      event.preventDefault();
      selectHighlighted();
    }
    if (event.key === "Escape") {
      event.preventDefault();
      setIsOpen(false);
      controlRef.current?.focus();
    }
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      moveHighlight(1);
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      moveHighlight(-1);
    }
    if (event.key === "Enter") {
      event.preventDefault();
      selectHighlighted();
    }
    if (event.key === "Escape") {
      event.preventDefault();
      setIsOpen(false);
      controlRef.current?.focus();
    }
  };

  const renderOption = (option, type) => {
    const selectedGroups = type === "member" ? selectedMemberGroups : selectedAdminGroups;
    const toggleGroup = type === "member" ? onToggleMemberGroup : onToggleAdminGroup;
    const checked = selectedGroups.includes(option.value);
    const optionKey = `${type}-${option.value}`;
    const optionIndex = optionIndexByKey.get(optionKey);
    const isHighlighted = highlightIndex === optionIndex;
    const optionTestId = buildAutomationOptionTestId(fieldTestId, `${type}-${option.value ?? option.label}`);

    return (
      <button
        key={option.value}
        type="button"
        ref={(node) => {
          if (optionIndex !== undefined) {
            optionRefs.current[optionIndex] = node;
          }
        }}
        className={`account-search-dropdown__option ${checked ? "is-selected" : ""} ${isHighlighted ? "is-highlighted" : ""}`.trim()}
        onClick={() => toggleGroup?.(option.value)}
        role="option"
        aria-selected={checked}
        data-testid={optionTestId}
      >
        <input
          type="checkbox"
          className="form-check-input"
          checked={checked}
          readOnly
          data-testid={buildAutomationChildTestId(optionTestId, "input")}
        />
        <span>{option.label ?? option.value}</span>
      </button>
    );
  };

  return (
    <div className={`account-search-dropdown ${className}`.trim()} ref={containerRef} data-testid={fieldTestId}>
      {label ? <div className="account-search-dropdown__label">{label}</div> : null}
      <button
        type="button"
        ref={controlRef}
        className={`account-search-dropdown__control ${isOpen ? "is-open" : ""} ${disabled ? "is-disabled" : ""}`.trim()}
        onClick={() => !disabled && setIsOpen((prev) => !prev)}
        onKeyDown={handleControlKeyDown}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={listId}
        disabled={disabled}
        data-testid={buildAutomationChildTestId(fieldTestId, "toggle")}
      >
        <span className={`account-search-dropdown__value ${visibleSelectedCount ? "" : "is-placeholder"}`.trim()}>
          {displayValue}
        </span>
        <span className="account-search-dropdown__icon" aria-hidden="true">
          <IoChevronDown size={16} />
        </span>
      </button>
      {isOpen ? (
        <div className="account-search-dropdown__panel" data-testid={buildAutomationChildTestId(fieldTestId, "menu")}>
          <div className="account-search-dropdown__panel-header">
            <span className="account-search-dropdown__count">
              {visibleSelectedCount ? `${visibleSelectedCount} selected` : "No selection"}
            </span>
            <div className="account-search-dropdown__actions">
              <button type="button" onClick={onSelectAll} data-testid={buildAutomationChildTestId(fieldTestId, "select-all")}>
                Select all
              </button>
              <button type="button" onClick={onClearAll} data-testid={buildAutomationChildTestId(fieldTestId, "clear")}>
                Clear
              </button>
            </div>
          </div>
          <input
            ref={searchRef}
            className="account-search-dropdown__search"
            type="text"
            placeholder="Search groups..."
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={handleSearchKeyDown}
            data-testid={buildAutomationChildTestId(fieldTestId, "input")}
          />
          <div className="account-search-dropdown__sections" role="listbox" aria-multiselectable="true" id={listId}>
            <div className="account-search-dropdown__section">
              <div className="account-search-dropdown__section-title">Group set</div>
              <button
                type="button"
                ref={(node) => {
                  const index = optionIndexByKey.get("group-set");
                  if (index !== undefined) {
                    optionRefs.current[index] = node;
                  }
                }}
                className={`account-search-dropdown__option account-search-dropdown__option--static ${highlightIndex === optionIndexByKey.get("group-set") ? "is-highlighted" : ""}`.trim()}
                onClick={() => onToggleMemberGroupSet?.(!allMemberGroupsSelected)}
                role="option"
                aria-selected={allMemberGroupsSelected}
                data-testid={buildAutomationChildTestId(fieldTestId, "group-set")}
              >
                <input
                  ref={groupSetRef}
                  type="checkbox"
                  className="form-check-input custom-checklist"
                  checked={allMemberGroupsSelected}
                  readOnly
                  data-testid={buildAutomationChildTestId(fieldTestId, "group-set-input")}
                />
                <span>{groupSetLabel}</span>
              </button>
            </div>
            <div className="account-search-dropdown__section">
              <div className="account-search-dropdown__section-title">Member groups</div>
              {filteredMemberOptions.length ? (
                filteredMemberOptions.map((option) => renderOption(option, "member"))
              ) : (
                <div className="account-search-dropdown__empty" data-testid={buildAutomationChildTestId(fieldTestId, "member-empty")}>
                  No member groups
                </div>
              )}
            </div>
            <div className="account-search-dropdown__section">
              <div className="account-search-dropdown__section-title">Admin groups</div>
              {filteredAdminOptions.length ? (
                filteredAdminOptions.map((option) => renderOption(option, "admin"))
              ) : (
                <div className="account-search-dropdown__empty" data-testid={buildAutomationChildTestId(fieldTestId, "admin-empty")}>
                  No admin groups
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default GroupDropdown;
